import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import InstallationModal from '~/components/InstallationModal';
import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import { useMFEContext } from '~/components/MFEProvider';
import useHostingInfo from '~/hooks/useHostingInfo';
import useHostingNavigate from '~/hooks/useHostingNavigate';

import { getSiteInstallingSlides } from '../../utils/helpers';
import LaunchSiteTypeModal from '../LaunchSiteTypeModal';

type BtnGroupPropOptions = {
  loading?: boolean;
  disabled?: boolean;
  hasCancelBtn?: boolean;
  domainChangeLoading?: boolean;
  hasFinishBtn?: boolean;
  hasSkipBtn?: boolean;
  showInstallationModal?: boolean;
  type?: string;
  btnType?: 'button' | 'submit' | 'reset';
  btnColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  btnText?: string;
  dataTestId?: string;
  onCancelHandler?: VoidFunction;
  onSkipHandler?: VoidFunction;
  onClickHandler?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCloseHandler?: VoidFunction;
};

const BtnGroup = ({
  loading = false,
  disabled = false,
  hasCancelBtn = false,
  hasSkipBtn = true,
  hasFinishBtn = false,
  domainChangeLoading = false,
  showInstallationModal = true,
  type = 'wordpress',
  btnType = undefined,
  btnColor = undefined,
  btnText = undefined,
  dataTestId = 'continue-btn',
  onCancelHandler = () => {},
  onSkipHandler = () => {},
  onClickHandler = undefined,
  onCloseHandler = undefined,
}: BtnGroupPropOptions) => {
  // @ts-expect-error
  const { brand } = useMFEContext();
  const { t } = useTranslation(['addSites', 'site']);
  // Shared Hosting - on site create was setting the type back to the defaultFormData
  // this will keep the selected type value
  // TODO: Update the Shared Hosting flow to match the Cloud Hosting add site flow
  const [siteType] = useState(type);
  const hostingNavigate = useHostingNavigate();
  const { hostingAccount } = useHostingInfo();
  const hostingType = hostingAccount?.data?.type;

  const slides = getSiteInstallingSlides(brand);

  const handleCloseInstallModal = () => {
    if (onCloseHandler) {
      onCloseHandler();
    } else {
      hostingNavigate();
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        {hasCancelBtn && (
          <Button
            variant="text"
            onClick={onCancelHandler}
            data-testid="cancel-btn"
          >
            {t('cancel')}
          </Button>
        )}
        {hasSkipBtn && (
          <Button variant="text" onClick={onSkipHandler} data-testid="skip-btn">
            {t('skipForNow')}
          </Button>
        )}
        <LoadingButtonWrapper
          type="submit"
          {...(disabled && { disabled })}
          {...(btnType && { type: btnType })}
          {...(btnColor && { color: btnColor })}
          {...(onClickHandler && { onClick: onClickHandler })}
          loading={loading || domainChangeLoading}
          data-testid={dataTestId}
        >
          {btnText ?? (hasFinishBtn ? t('finish') : t('continue'))}
        </LoadingButtonWrapper>
      </Stack>
      {showInstallationModal &&
        (siteType === 'wordpress' && hostingType !== 'cluster_atomic' ? (
          <InstallationModal
            slides={slides}
            open={loading}
            showClose
            circular={false}
            onClose={handleCloseInstallModal}
            bottomBar={{
              node: (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography fontWeight={600}>
                    {t('site:firstSiteInstall.installing')}
                  </Typography>
                </Stack>
              ),
            }}
          />
        ) : (
          <LaunchSiteTypeModal
            open={loading}
            showClose
            onClose={handleCloseInstallModal}
          />
        ))}
    </>
  );
};

export default BtnGroup;
